import { apiClient } from '@/services/apiClient'

export interface SMARTCriterion {
  score: number
  feedback: string
  suggestions: string[]
}

export interface CompleteCascadePath {
  strategic_objective_id: number
  strategic_objective_code: string
  strategic_objective_name: string
  strategic_initiative_id: number
  strategic_initiative_code: string
  strategic_initiative_name: string
  operational_initiative_id: number
  operational_initiative_code: string
  operational_initiative_name: string
  is_recommended?: boolean  // AI's top recommendation
  confidence?: number  // Confidence for this specific path
  rationale?: string  // Why this path is recommended
}

export interface StrategicAlignmentSuggestion {
  strategic_objective_id: number
  strategic_objective_name: string
  strategic_objective_code: string
  confidence: number
  rationale: string
  complete_paths?: CompleteCascadePath[]
  // Legacy support (deprecated)
  strategic_initiative_suggestions?: Array<{
    id: number
    code: string
    name: string
  }>
}

export interface StrategicAlignment {
  alignment_score: number
  alignment_level: string
  feedback: string
  strengths: string[]
  gaps: string[]
  recommendations: string[]
}

export interface SMARTAnalysisResult {
  specific: SMARTCriterion
  measurable: SMARTCriterion
  achievable: SMARTCriterion
  relevant: SMARTCriterion
  timeBound: SMARTCriterion
  overallScore: number
  strategic_alignment?: StrategicAlignment
  strategic_suggestions?: StrategicAlignmentSuggestion[]
}

export interface SMARTAnalysisRequest {
  title: string
  description: string
  targetValue?: number
  unit?: string
  startDate?: string
  endDate?: string
  kpis?: Array<{
    name: string
    description?: string
    target_value?: number | string
    unit?: string
    current_value?: number
  }>
  strategicInitiative?: {
    name: string
    description?: string
  }
  operationalInitiatives?: Array<{
    name: string
    description?: string
  }>
  // Strategic context (5-level framework) - optional
  kpa_name?: string
  // L1: Strategic Objective
  strategic_objective_id?: number
  strategic_objective?: {
    code: string
    name: string
    description?: string
  }
  // L2: Strategic Initiative
  strategic_initiative_id?: number
  strategic_initiative?: {
    code: string
    name: string
    description?: string
  }
  // L3: Operational Initiative
  operational_initiative_id?: number
  operational_initiative?: {
    code: string
    name: string
    description?: string
  }
}

export interface AIInsight {
  id: string
  type: string
  title: string
  description: string
  confidence: number
  impact: 'high' | 'medium' | 'low'
  category: string
  recommendedActions: string[]
  timestamp: string
  relatedObjectives?: string[]
  relatedKPIs?: string[]
  actionable: boolean
}

export interface ObjectiveInsightsRequest {
  objectiveId: string
  title: string
  description: string
  progress?: number
  status?: string
  kpis?: Array<{
    name: string
    current_value?: number
    target_value?: number
    unit?: string
  }>
  strategicContext?: {
    pillar?: string
    kpa?: string
  }
}

export interface KPAData {
  id: string
  name: string
  description?: string
  objectives: Array<any>
  kpis: Array<any>
  evidence: Array<any>
}

// Stakeholder feedback summary for AI enrichment
// Note: keyStrengths, developmentAreas, and recommendations can be either strings
// or structured objects from the holistic analysis API
export interface StakeholderFeedbackSummary {
  available: boolean
  totalFeedbackCycles?: number
  totalResponses?: number
  overallSummary?: string
  keyStrengths?: Array<string | { theme: string; description: string; evidence_count?: number; sentiment?: string }>
  developmentAreas?: Array<string | { theme: string; description: string; evidence_count?: number; sentiment?: string }>
  themes?: Array<{
    theme: string
    sentiment: 'positive' | 'neutral' | 'negative'
    frequency: number
  }>
  trendDirection?: 'improving' | 'stable' | 'declining'
  recommendations?: Array<string | { priority?: string; action: string; expected_impact?: string; timeframe?: string }>
}

export interface ReviewPreparationRequest {
  employeeId: string
  employeeName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin'
  kpas: KPAData[]
  reviewDate?: string
  stakeholderFeedback?: StakeholderFeedbackSummary
  // 'self' = team member preparing for their own check-in (second person);
  // 'manager' = people leader reviewing the team member (third person).
  perspective?: 'self' | 'manager'
}

export interface KPAReadiness {
  kpaId: string
  kpaName: string
  readinessScore: number
  strengths: string[]
  gaps: string[]
  suggestedDiscussionPoints: string[]
}

export interface EvidenceQuality {
  total: number
  withMeasurements: number
  qualityScore: number
  suggestions: string[]
}

export interface ReviewPreparationInsights {
  kpaReadiness: KPAReadiness[]
  overallReadiness: number
  recommendedFocus: string[]
  evidenceQuality: EvidenceQuality
  generatedAt: string
}

export interface KPADiscussionRequest {
  kpaId: string
  kpaName: string
  kpaDescription?: string
  objectives: Array<any>
  kpis: Array<any>
  evidence: Array<any>
  employeeName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin'
  existingDiscussionPoints?: {
    wentWell?: Array<any>
    challenges?: Array<any>
    actions?: Array<any>
  }
  stakeholderFeedback?: StakeholderFeedbackSummary
}

export interface EvidenceHighlight {
  evidenceId: string
  evidenceTitle: string
  reason: string
  category: 'exceptional' | 'needs_discussion' | 'missing_context'
}

export interface KPADiscussionGuidance {
  kpaId: string
  celebrationPrompts: string[]
  rootCauseQuestions: string[]
  developmentSuggestions: string[]
  evidenceHighlights: EvidenceHighlight[]
  discussionTips: string[]
  generatedAt: string
}

// Tier 3: Rating Rationale Support
export interface RatingRationaleRequest {
  kpaId: string
  kpaName: string
  kpaDescription?: string
  objectives: Array<any>
  kpis: Array<any>
  evidence: Array<any>
  discussionPoints?: {
    wentWell?: Array<any>
    challenges?: Array<any>
    actions?: Array<any>
  }
  employeeName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin'
  proposedRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  stakeholderFeedback?: StakeholderFeedbackSummary
}

export interface BiasWarning {
  type: 'recency' | 'leniency' | 'strictness' | 'halo' | 'horn'
  severity: 'low' | 'medium' | 'high'
  description: string
  mitigation: string
}

export interface RatingRationale {
  kpaId: string
  suggestedRating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  confidenceScore: number // 0-100
  evidenceBasedJustification: string[]
  strengthsSupporting: string[]
  gapsOrConcerns: string[]
  calibrationNotes: string[]
  biasWarnings: BiasWarning[]
  alternativeRatingConsiderations?: string
  generatedAt: string
}

// Tier 4: Post-Review Synthesis
export interface ReviewSynthesisRequest {
  employeeId: string
  employeeName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin'
  reviewDate?: string
  kpaRatings: Array<{
    kpaId: string
    kpaName: string
    rating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
    /** The People Leader's written justification for this KPA's rating. */
    justification?: string
  }>
  overallRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  /** The People Leader's overall written justification for the rating. */
  overallJustification?: string
  allDiscussionPoints: Record<string, {
    wentWell?: Array<any>
    challenges?: Array<any>
    actions?: Array<any>
  }>
  allEvidence: Array<any>
  stakeholderFeedback?: StakeholderFeedbackSummary
  isFormal?: boolean
}

export interface StrengthPattern {
  theme: string
  description: string
  supportingKPAs: string[]
  evidenceCount: number
}

export interface DevelopmentPriority {
  area: string
  importance: 'high' | 'medium' | 'low'
  rationale: string
  suggestedActions: string[]
  timeline: '30-day' | '90-day' | '180-day'
}

export interface IDPSuggestion {
  focusArea: string
  developmentGoal: string
  suggestedActivities: string[]
  successMetrics: string[]
  resources: string[]
}

export interface ReviewSynthesis {
  employeeId: string
  employeeName: string
  reviewType: string
  overallRating?: string | null
  keyAccomplishments: string[]
  strengthPatterns: StrengthPattern[]
  developmentPriorities: DevelopmentPriority[]
  idpSuggestions: IDPSuggestion[]
  careerTrajectoryInsights: string[]
  followUpTimeline: {
    '30-day': string[]
    '90-day': string[]
    '180-day': string[]
  }
  managerNotes: string[]
  generatedAt: string
}

class AIService {
  constructor() {
    // AI requests are routed through the gateway to reach internal AI service
  }

  /**
   * Perform SMART analysis on an objective
   */
  async analyzeSMART(request: SMARTAnalysisRequest): Promise<SMARTAnalysisResult> {
    try {
      const response = await apiClient.post<SMARTAnalysisResult>(
        '/ai/api/smart/analyze',
        request
      )
      return response.data
    } catch (error) {
      console.error('SMART analysis failed:', error)
      throw new Error('Failed to perform SMART analysis. Please try again.')
    }
  }

  /**
   * Get improvement suggestions for an objective
   */
  async getImprovementSuggestions(request: SMARTAnalysisRequest): Promise<{
    improvements: string[]
    refinedTitle?: string
    refinedDescription?: string
    confidenceScore: number
  }> {
    try {
      const response = await apiClient.post<{
        improvements: string[]
        refinedTitle?: string
        refinedDescription?: string
        confidenceScore: number
      }>(
        '/ai/api/smart/suggestions',
        request
      )
      return response.data
    } catch (error) {
      console.error('Suggestion generation failed:', error)
      throw new Error('Failed to generate suggestions. Please try again.')
    }
  }

  /**
   * Quick SMART score (faster, lighter)
   */
  async quickScore(title: string, description: string): Promise<{
    score: number
    assessment: string
    weakestCriterion: string
  }> {
    try {
      const response = await apiClient.post<{
        score: number
        assessment: string
        weakestCriterion: string
      }>(
        '/ai/api/smart/quick-score',
        null,
        { params: { title, description } }
      )
      return response.data
    } catch (error) {
      console.error('Quick score failed:', error)
      throw new Error('Failed to calculate quick score. Please try again.')
    }
  }

  /**
   * Get AI-powered strategic alignment suggestions
   * Returns top 3 strategic objectives with confidence scores and rationale
   */
  async getStrategicSuggestions(
    title: string,
    description: string,
    kpaName?: string
  ): Promise<StrategicAlignmentSuggestion[]> {
    try {
      const response = await apiClient.post<StrategicAlignmentSuggestion[]>(
        '/ai/api/smart/strategic-suggestions',
        null,
        {
          params: { 
            title, 
            description,
            kpa_name: kpaName 
          }
        }
      )
      return response.data
    } catch (error) {
      console.error('Strategic suggestions failed:', error)
      throw new Error('Failed to generate strategic suggestions. Please try again.')
    }
  }

  /**
   * Generate AI insights for an objective
   */
  async generateObjectiveInsights(request: ObjectiveInsightsRequest): Promise<AIInsight[]> {
    try {
      const response = await apiClient.post<{ insights: AIInsight[] }>(
        '/ai/api/insights/objectives',
        request
      )
      return response.data.insights
    } catch (error) {
      console.error('Insights generation failed:', error)
      throw new Error('Failed to generate insights. Please try again.')
    }
  }

  /**
   * Generate review preparation insights
   * Provides evidence-based readiness analysis for performance reviews
   */
  async generateReviewPreparationInsights(
    request: ReviewPreparationRequest
  ): Promise<ReviewPreparationInsights> {
    try {
      const response = await apiClient.post<ReviewPreparationInsights>(
        '/ai/api/insights/review/preparation',
        request
      )
      return response.data
    } catch (error) {
      console.error('Review preparation insights generation failed:', error)
      throw new Error('Failed to generate check-in preparation insights. Please try again.')
    }
  }

  /**
   * Generate KPA discussion guidance
   * Provides contextual assistance during review conversations
   */
  async generateKPADiscussionGuidance(
    request: KPADiscussionRequest
  ): Promise<KPADiscussionGuidance> {
    try {
      const response = await apiClient.post<KPADiscussionGuidance>(
        '/ai/api/insights/review/kpa-guidance',
        request
      )
      return response.data
    } catch (error) {
      console.error('KPA discussion guidance generation failed:', error)
      throw new Error('Failed to generate discussion guidance. Please try again.')
    }
  }

  /**
   * Generate rating rationale with bias detection (Tier 3)
   * Provides evidence-based rating suggestions and fairness checks
   */
  async generateRatingRationale(
    request: RatingRationaleRequest
  ): Promise<RatingRationale> {
    try {
      const response = await apiClient.post<RatingRationale>(
        '/ai/api/insights/review/rating-rationale',
        request
      )
      return response.data
    } catch (error) {
      console.error('Rating rationale generation failed:', error)
      throw new Error('Failed to generate indicator rationale. Please try again.')
    }
  }

  /**
   * Generate comprehensive review synthesis (Tier 4)
   * Creates post-review summary with IDP suggestions and follow-up timeline
   */
  async generateReviewSynthesis(
    request: ReviewSynthesisRequest
  ): Promise<ReviewSynthesis> {
    try {
      const response = await apiClient.post<ReviewSynthesis>(
        '/ai/api/insights/review/synthesis',
        request
      )
      return response.data
    } catch (error) {
      console.error('Review synthesis generation failed:', error)
      throw new Error('Failed to generate check-in synthesis. Please try again.')
    }
  }

  /**
   * Check AI service health
   */
  async healthCheck(): Promise<{
    status: string
    timestamp: string
    azure_openai: string
    models_available: string[]
  }> {
    try {
      const response = await apiClient.get<{
        status: string
        timestamp: string
        azure_openai: string
        models_available: string[]
      }>('/ai/health')
      return response.data
    } catch (error) {
      console.error('AI service health check failed:', error)
      throw new Error('AI service is unavailable')
    }
  }
}

export const aiService = new AIService()
